import React, { useState, forwardRef } from "react";
import { Upload, X, FileText, Loader2, Phone, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const SERVICE_OPTS = [
  { v: "Flex Printing", en: "Flex Printing", kn: "ಫ್ಲೆಕ್ಸ್ ಪ್ರಿಂಟಿಂಗ್" },
  { v: "Vinyl Printing", en: "Vinyl Printing", kn: "ವಿನೈಲ್ ಪ್ರಿಂಟಿಂಗ್" },
  { v: "Sun Board", en: "Sun Board", kn: "ಸನ್ ಬೋರ್ಡ್" },
  { v: "LED Name Board", en: "LED Name Board", kn: "LED ನೇಮ್ ಬೋರ್ಡ್" },
  { v: "Custom / Other", en: "Custom / Other", kn: "ಕಸ್ಟಮ್ / ಇತರೆ" },
];

const initial = { name: "", phone: "", service: "", dimensions: "", quantity: "", installation: false, delivery_date: "", message: "" };

export const QuoteForm = forwardRef(({ presetService }, ref) => {
  const { lang, tr } = useLang();
  const { business, waLink } = useContent();
  const kn = lang === "kn";
  const [form, setForm] = useState(initial);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  React.useEffect(() => {
    if (presetService) setForm((f) => ({ ...f, service: presetService }));
  }, [presetService]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFiles = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    setUploading(true);
    for (const file of selected) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, data]);
      } catch (err) {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service) {
      toast.error(tr("f_required"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source: "web", files });
      setDone(true);
      toast.success(tr("quote_success"));
      setForm(initial);
      setFiles([]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || tr("quote_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" ref={ref} className="py-16 lg:py-24 bg-slate-50 cp-grid-texture scroll-mt-20">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
          <div className="grid lg:grid-cols-5">
            <div className="lg:col-span-2 bg-slate-900 p-7 lg:p-9 text-white flex flex-col">
              <p className={`text-xs font-bold uppercase tracking-widest text-amber-400 ${kn ? "font-kn" : ""}`}>{tr("quote_kicker")}</p>
              <h2 className={`mt-2 text-2xl lg:text-3xl font-bold ${kn ? "font-kn" : "font-heading"}`}>{tr("quote_title")}</h2>
              <p className={`mt-3 text-sm text-slate-300 leading-relaxed ${kn ? "font-kn" : ""}`}>{tr("quote_sub")}</p>
              <div className="mt-auto pt-8 space-y-3">
                <a href={`tel:${business.phone}`} data-testid="quote-side-call" className="flex items-center gap-3 rounded-xl bg-white/10 px-4 py-3 hover:bg-white/20 transition-colors">
                  <Phone size={18} className="text-amber-400" />
                  <span className="text-sm font-semibold">{business.phoneDisplay}</span>
                </a>
                <a href={waLink("Hi Creative Prints, I have a print enquiry.")} target="_blank" rel="noreferrer" data-testid="quote-side-wa" className="flex items-center gap-3 rounded-xl bg-[#25D366]/15 px-4 py-3 hover:bg-[#25D366]/25 transition-colors">
                  <MessageCircle size={18} className="text-[#25D366]" />
                  <span className="text-sm font-semibold">WhatsApp {business.phoneDisplay}</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-3 p-7 lg:p-9">
              {done ? (
                <div data-testid="quote-success" className="flex flex-col items-center justify-center text-center h-full py-10">
                  <CheckCircle2 size={56} className="text-green-500" />
                  <h3 className={`mt-4 text-xl font-bold text-slate-900 ${kn ? "font-kn" : "font-heading"}`}>{tr("quote_success")}</h3>
                  <Button className="mt-6" variant="outline" onClick={() => setDone(false)} data-testid="quote-new-btn">+ New enquiry</Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="quote-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className={kn ? "font-kn" : ""}>{tr("f_name")} *</Label>
                      <Input data-testid="quote-name" value={form.name} onChange={set("name")} className="mt-1.5" placeholder={tr("f_name")} />
                    </div>
                    <div>
                      <Label className={kn ? "font-kn" : ""}>{tr("f_phone")} *</Label>
                      <Input data-testid="quote-phone" value={form.phone} onChange={set("phone")} className="mt-1.5" placeholder="+91" />
                    </div>
                  </div>

                  <div>
                    <Label className={kn ? "font-kn" : ""}>{tr("f_service")} *</Label>
                    <select
                      data-testid="quote-service"
                      value={form.service}
                      onChange={set("service")}
                      className="mt-1.5 w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">{tr("f_choose")}</option>
                      {SERVICE_OPTS.map((o) => (
                        <option key={o.v} value={o.v}>{kn ? o.kn : o.en}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className={kn ? "font-kn" : ""}>{tr("f_dimensions")}</Label>
                      <Input data-testid="quote-dimensions" value={form.dimensions} onChange={set("dimensions")} className="mt-1.5" placeholder="6ft x 3ft" />
                    </div>
                    <div>
                      <Label className={kn ? "font-kn" : ""}>{tr("f_quantity")}</Label>
                      <Input data-testid="quote-quantity" value={form.quantity} onChange={set("quantity")} className="mt-1.5" placeholder="1" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className={kn ? "font-kn" : ""}>{tr("f_delivery")}</Label>
                      <Input data-testid="quote-delivery" type="date" value={form.delivery_date} onChange={set("delivery_date")} className="mt-1.5" />
                    </div>
                    <div>
                      <Label className={kn ? "font-kn" : ""}>{tr("f_install")}</Label>
                      <div className="mt-1.5 flex gap-2">
                        {[{ v: true, t: tr("f_yes") }, { v: false, t: tr("f_no") }].map((opt) => (
                          <button
                            key={String(opt.v)}
                            type="button"
                            data-testid={`quote-install-${opt.v}`}
                            onClick={() => setForm((f) => ({ ...f, installation: opt.v }))}
                            className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${form.installation === opt.v ? "border-primary bg-amber-50 text-primary" : "border-input text-slate-600"}`}
                          >
                            {opt.t}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className={kn ? "font-kn" : ""}>{tr("f_message")}</Label>
                    <Textarea data-testid="quote-message" value={form.message} onChange={set("message")} className="mt-1.5" rows={3} />
                  </div>

                  <div>
                    <Label className={kn ? "font-kn" : ""}>{tr("f_artwork")}</Label>
                    <label
                      data-testid="quote-upload-label"
                      className="mt-1.5 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center hover:border-primary transition-colors"
                    >
                      {uploading ? <Loader2 className="animate-spin text-primary" /> : <Upload className="text-slate-400" />}
                      <span className={`text-sm font-semibold text-slate-600 ${kn ? "font-kn" : ""}`}>
                        {uploading ? tr("f_uploading") : tr("f_upload_btn")}
                      </span>
                      <span className={`text-xs text-slate-400 ${kn ? "font-kn" : ""}`}>{tr("f_artwork_hint")}</span>
                      <input data-testid="quote-file-input" type="file" multiple className="hidden" onChange={handleFiles} />
                    </label>

                    {files.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {files.map((f) => (
                          <div key={f.id} data-testid={`quote-file-${f.id}`} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                            <FileText size={16} className="text-primary shrink-0" />
                            <span className="flex-1 truncate text-sm text-slate-700">{f.original_filename}</span>
                            <button type="button" onClick={() => removeFile(f.id)} className="text-slate-400 hover:text-red-500">
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button data-testid="quote-submit" type="submit" disabled={submitting || uploading} className="w-full gap-2 bg-primary hover:bg-amber-700 text-white font-semibold h-11">
                    {submitting ? <><Loader2 size={18} className="animate-spin" /> {tr("f_submitting")}</> : tr("f_submit")}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

QuoteForm.displayName = "QuoteForm";
